import { Crown, CreditCard, MonitorSmartphone, ShieldCheck, ShoppingCart } from 'lucide-react';
import Reveal from './Reveal';
import predatorBadlands from '../assets/series/predator-badlands.webp';
import championsLeague from '../assets/sports/championsleague02.jpg';
import moneyHeist from '../assets/series/money-heist-s6.jpg';

const DEFAULT_FEATURES = [
    { icon: CreditCard, line1: 'Pago único', line2: '(sin suscripción)' },
    { icon: MonitorSmartphone, line1: '2 pantallas simultáneas' },
    { icon: Crown, line1: 'Acceso completo al', line2: 'plan Premium' },
    { icon: ShieldCheck, line1: 'Garantía de devolución', line2: 'de 30 días' },
];

const DEFAULT_IMAGES = {
    left: { src: predatorBadlands, alt: 'Predator: Badlands' },
    center: { src: championsLeague, alt: 'UEFA Champions League' },
    right: { src: moneyHeist, alt: 'Money Heist Temporada 6' },
};

export default function LifetimeOfferCard({
    badgeText = 'Oferta limitada',
    title = 'Plan de por vida',
    description = 'Un único pago, acceso permanente a tu suscripción premium: sin renovaciones ni sorpresas.',
    features = DEFAULT_FEATURES,
    images = DEFAULT_IMAGES,
    price = '159',
    priceCents = '00',
    currency = '€',
    priceNote = 'pago único',
    ctaLabel = 'Pedir',
    securityLabel = 'Pago 100% seguro',
    onOrder,
}) {
    return (
        <Reveal className="lifetime-offer relative overflow-hidden rounded-[20px] p-6 sm:p-9 lg:p-11">
            <div className="absolute inset-0 lifetime-offer-glow" aria-hidden="true"></div>
            <div className="absolute inset-0 lifetime-offer-dots" aria-hidden="true"></div>

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr_0.82fr] gap-10 lg:gap-10 items-center">
                {/* LEFT: badge, heading, description, features */}
                <div className="flex flex-col gap-5 min-w-0">
                    <span className="lifetime-badge">
                        <Crown size={14} strokeWidth={2.5} />
                        {badgeText}
                    </span>

                    <h2 className="text-3xl sm:text-4xl lg:text-[2.65rem] font-black text-white uppercase leading-[1.05] tracking-tight">
                        {title}
                    </h2>

                    <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-md">
                        {description}
                    </p>

                    <div className="grid grid-cols-2 gap-3 mt-1">
                        {features.map((f) => {
                            const Icon = f.icon;
                            return (
                                <div key={f.line1} className="lifetime-feature">
                                    <span className="lifetime-feature-icon">
                                        <Icon size={18} strokeWidth={2} />
                                    </span>
                                    <span className="text-white text-[13px] sm:text-sm font-medium leading-snug min-w-0 break-words">
                                        {f.line1}
                                        {f.line2 && <><br />{f.line2}</>}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* CENTER: overlapping promotional image stack */}
                <div className="lifetime-stack" role="group" aria-label="Vista previa del contenido premium">
                    <div className="lifetime-card lifetime-card-left">
                        <img src={images.left.src} alt={images.left.alt} loading="lazy" />
                    </div>
                    <div className="lifetime-card lifetime-card-main">
                        <img src={images.center.src} alt={images.center.alt} loading="lazy" />
                    </div>
                    <div className="lifetime-card lifetime-card-right">
                        <img src={images.right.src} alt={images.right.alt} loading="lazy" />
                    </div>
                </div>

                {/* RIGHT: price + CTA */}
                <div className="lifetime-price-panel">
                    <div className="flex items-end justify-center gap-1">
                        <span className="text-[2.75rem] sm:text-5xl font-black text-white leading-none">{price}</span>
                        <span className="text-xl sm:text-2xl font-bold text-white/80 leading-none mb-1">,{priceCents}{currency}</span>
                    </div>
                    <p className="text-white/50 text-xs mt-2">{priceNote}</p>

                    <button
                        onClick={onOrder}
                        className="btn-glow btn-glow-gold gold-gradient text-ink w-full mt-5 py-3.5 rounded-full font-bold text-base flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.03]"
                    >
                        <span className="flex items-center gap-2">
                            <ShoppingCart size={18} strokeWidth={2.5} />
                            {ctaLabel}
                        </span>
                    </button>

                    <p className="text-white/50 text-xs mt-4 flex items-center justify-center gap-1.5">
                        <ShieldCheck size={14} strokeWidth={2} />
                        {securityLabel}
                    </p>
                </div>
            </div>
        </Reveal>
    );
}
